import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Plus, Trash2, ShieldCheck, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const isDomainOrEmail = (v) => {
  if (!v) return false;
  const clean = v.trim().toLowerCase().replace(/^@/, "");
  if (clean.includes("@")) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean);
  return /^[^\s@]+\.[^\s@]+$/.test(clean);
};

export default function InvoiceEmailSettings() {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [autoApprove, setAutoApprove] = useState(false);
  const [inbound, setInbound] = useState("");
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [secretHint, setSecretHint] = useState(null);
  const [newEntry, setNewEntry] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [{ data: al }, { data: st }] = await Promise.all([
        api.get("/invoice_email_allowlist"),
        api.get("/pharmacy-profile/invoice-email"),
      ]);
      setEntries(al || []);
      setAutoApprove(!!st.auto_approve);
      setInbound(st.inbound_endpoint || "");
      setSecretConfigured(!!st.secret_configured);
      setSecretHint(st.secret_hint || null);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const add = async () => {
    const val = newEntry.trim().toLowerCase().replace(/^@/, "");
    if (!isDomainOrEmail(val)) return toast.error("Enter a valid email (a@b.com) or domain (mckesson.com)");
    if (entries.some((e) => (e.email_or_domain || "").toLowerCase() === val)) {
      return toast.error("Already in the allow-list");
    }
    setBusy(true);
    try {
      await api.post("/invoice_email_allowlist", { email_or_domain: val });
      setNewEntry("");
      toast.success(`Added ${val}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/invoice_email_allowlist/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleAuto = async (val) => {
    setAutoApprove(val);
    try { await api.put("/pharmacy-profile/invoice-email", { auto_approve: val });
          toast.success(val ? "Auto-approve ON (high-confidence captures skip review)" : "Every emailed invoice goes to Review"); }
    catch (e) { setAutoApprove(!val); toast.error(e.response?.data?.detail || "Failed"); }
  };

  const inboundUrl = inbound
    ? `${window.location.origin}${inbound}${secretConfigured ? "?secret=YOUR_SECRET_HERE" : ""}`
    : "";
  const copy = () => { navigator.clipboard.writeText(inboundUrl); toast.success("Webhook URL copied"); };

  if (!isAdmin) return null;

  return (
    <Card className="p-5 border-slate-200 mb-6" data-testid="invoice-email-settings">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-5 h-5 text-pink-700" />
        <h3 className="font-heading font-semibold text-slate-800">Email-in invoices</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Suppliers on the allow-list can email invoices directly to Vera — attachments are AI-extracted and queued for review.
        Set up your SendGrid Inbound Parse on <span className="font-mono text-slate-700">verafii.net</span> to POST to the webhook below.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/40">
          <Label className="text-xs text-slate-500 mb-1 block">SendGrid Inbound webhook</Label>
          <div className="flex gap-2">
            <Input data-testid="inbound-url" value={inboundUrl} readOnly className="font-mono text-xs h-9" />
            <Button data-testid="inbound-copy" variant="outline" size="sm" onClick={copy} className="h-9 gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Copy
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            DNS: add MX record on <span className="font-mono">verafii.net</span> → <span className="font-mono">mx.sendgrid.net</span> (priority 10).
          </p>
          {secretConfigured ? (
            <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Webhook secret enforced ({secretHint || "configured"}) — replace <span className="font-mono">YOUR_SECRET_HERE</span> with the real secret when configuring SendGrid. SPF-fail senders are also rejected.
            </p>
          ) : (
            <p className="text-[11px] text-amber-700 mt-1">
              ⚠ No webhook secret set — anyone who guesses the URL could post. Set <span className="font-mono">SENDGRID_INBOUND_SECRET</span> in backend env to enforce.
            </p>
          )}
        </div>

        <div className="p-3 rounded-lg border border-pink-100 bg-pink-50/40">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-xs text-pink-900 font-medium">Auto-approve high-confidence captures</Label>
              <p className="text-[11px] text-slate-500 mt-1">
                When OFF (default), every emailed invoice waits in <span className="font-semibold">Review</span>.
                When ON, invoices where the AI extracted both <span className="font-mono">vendor</span> and <span className="font-mono">total &gt; 0</span> are auto-verified.
              </p>
            </div>
            <Switch data-testid="auto-approve-toggle" checked={autoApprove} onCheckedChange={toggleAuto} />
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-pink-700" />
          Sender allow-list ({entries.length})
        </Label>
        <div className="flex gap-2 mb-3">
          <Input data-testid="allowlist-input" placeholder="mckesson.com  or  billing@cardinal.com"
            value={newEntry} onChange={(e) => setNewEntry(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="font-mono h-9" />
          <Button data-testid="allowlist-add" onClick={add} disabled={busy}
            className="bg-pink-700 hover:bg-pink-800 gap-1.5 h-9">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </Button>
        </div>
        {entries.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No senders yet — Vera will reject every inbound email until you add one.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {entries.map((e) => (
              <div key={e.id} data-testid={`allowlist-row-${e.id}`}
                className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white border border-slate-200 text-sm">
                <span className="font-mono text-slate-700">
                  {(e.email_or_domain || "").includes("@") ? "" : "@"}{e.email_or_domain}
                </span>
                <button data-testid={`allowlist-remove-${e.id}`} onClick={() => remove(e.id)}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
